import { cn } from "@/lib/utils";
import { Popover, PopoverTrigger, PopoverContent } from "./popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandGroup,
  CommandItem,
} from "./command";
import { ChevronsUpDown, Check, LucideIcon } from "lucide-react";
import React from "react";
import { Button, buttonVariants } from "./button";
import { useCappedList } from "@/hooks/useCappedList";
import { VariantProps } from "class-variance-authority";
import { useCommandState } from "cmdk";
import { useLingui } from "@lingui/react/macro";

/**
 * Parses a prefixed value created by MultiSelectItem's valuePrefix prop.
 * Returns the prefix and the original value.
 */
function parseMultiSelectValue(prefixedValue: string): {
  prefix: string | null;
  value: string;
} {
  const dashIndex = prefixedValue.indexOf("-");
  if (dashIndex === -1) {
    return { prefix: null, value: prefixedValue };
  }
  return {
    prefix: prefixedValue.substring(0, dashIndex),
    value: prefixedValue.substring(dashIndex + 1),
  };
}

/**
 * Groups an array of prefixed values by their prefix.
 * Useful for processing MultiSelect form submissions.
 */
function groupMultiSelectValues(
  prefixedValues: string[]
): Record<string, string[]> {
  const grouped: Record<string, string[]> = {};

  for (const prefixedValue of prefixedValues) {
    const { prefix, value } = parseMultiSelectValue(prefixedValue);
    const key = prefix ?? "_unprefixed";
    if (!grouped[key]) {
      grouped[key] = [];
    }
    grouped[key].push(value);
  }

  return grouped;
}

// Context to share state between compound components
type MultiSelectContextValue = {
  onChange: (value: string[]) => void;
  onCreate?: (value: string) => void;
  getLabel: (value: string) => string;
  value: string[];
  open: boolean;
};

const MultiSelectContext = React.createContext<MultiSelectContextValue | null>(
  null
);

function useMultiSelectContext() {
  const context = React.useContext(MultiSelectContext);
  if (!context) {
    throw new Error(
      "MultiSelect compound components must be used within <MultiSelect>"
    );
  }
  return context;
}

type MultiSelectProps = {
  onChange: (value: string[]) => void;
  onCreate?: (value: string) => void;
  getLabel: (value: string) => string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  value: string[];
};

function MultiSelect({
  defaultOpen = false,
  children,
  onChange,
  onCreate,
  getLabel,
  value,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(defaultOpen);

  return (
    <MultiSelectContext.Provider
      value={{
        value,
        onChange,
        onCreate,
        open,
        getLabel,
      }}
    >
      <Popover modal={true} open={open} onOpenChange={setOpen}>
        {children}
      </Popover>
    </MultiSelectContext.Provider>
  );
}

function MultiSelectTrigger({
  id,
  className,
  placeholder,
  icon: Icon,
  size = "default",
  variant = "outline",
  withChevron = true,
}: {
  id?: string;
  icon?: LucideIcon;
  className?: string;
  placeholder?: string;
  withChevron?: boolean;
  size?: VariantProps<typeof buttonVariants>["size"];
  variant?: VariantProps<typeof buttonVariants>["variant"];
}) {
  const { open, getLabel, value } = useMultiSelectContext();

  const formatCappedList = useCappedList();

  return (
    <PopoverTrigger asChild>
      <Button
        id={id}
        size={size}
        variant={variant}
        role="combobox"
        aria-expanded={open}
        className={cn("justify-between text-left", className)}
      >
        {Icon && <Icon />}
        <span
          className={cn(
            "flex-1 truncate",
            !value.length && "text-muted-foreground hover:text-muted-foreground"
          )}
        >
          {value.length
            ? formatCappedList(value.map(getLabel), { cap: 3 })
            : placeholder}
        </span>
        {withChevron && <ChevronsUpDown className="opacity-50" />}
      </Button>
    </PopoverTrigger>
  );
}

function MultiSelectContent({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <PopoverContent
      className={cn("p-0 w-(--radix-popover-trigger-width)", className)}
      align="start"
    >
      <Command>{children}</Command>
    </PopoverContent>
  );
}

function MultiSelectSearch({ placeholder }: { placeholder: string }) {
  const { t } = useLingui();
  const { onCreate } = useMultiSelectContext();

  const count = useCommandState((state) => state.filtered.count);
  const value = useCommandState((state) => state.value);
  const search = useCommandState((state) => state.search.trim());

  const canCreate =
    onCreate && search && search.toLowerCase() !== value?.toLowerCase();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!canCreate) return;

    const shouldCreate =
      (e.key === "Enter" && count === 0) || (e.key === "Enter" && e.shiftKey);

    if (shouldCreate) {
      e.preventDefault();
      onCreate(search);
    }
  };

  const shortcut = canCreate ? (count ? t`Skapa ⇧+↩` : t`Skapa ↩`) : undefined;

  return (
    <CommandInput
      onKeyDown={handleKeyDown}
      shortcut={shortcut}
      placeholder={placeholder}
      className="h-9"
      onCreate={
        onCreate
          ? () => {
              onCreate(search);
            }
          : undefined
      }
    />
  );
}

function MultiSelectGroup({
  heading,
  children,
}: {
  heading?: string;
  children: React.ReactNode;
}) {
  return <CommandGroup heading={heading}>{children}</CommandGroup>;
}

function MultiSelectList({ children }: { children: React.ReactNode }) {
  return <CommandList className="max-h-64">{children}</CommandList>;
}

function MultiSelectItem({
  value: _value,
  valuePrefix,
  label,
}: {
  valuePrefix?: string;
  value: string;
  label: string;
}) {
  const { value: selectedValues, onChange } = useMultiSelectContext();

  const value = valuePrefix ? `${valuePrefix}-${_value}` : _value;

  const isSelected = selectedValues.includes(value);

  return (
    <CommandItem
      value={value}
      keywords={[value, label]}
      onSelect={() => {
        onChange(
          isSelected
            ? selectedValues.filter((v) => v !== value)
            : [...selectedValues, value]
        );
      }}
    >
      {label}
      <Check
        className={cn("ml-auto", isSelected ? "opacity-100" : "opacity-0")}
      />
    </CommandItem>
  );
}

export {
  MultiSelect,
  MultiSelectTrigger,
  MultiSelectContent,
  MultiSelectSearch,
  MultiSelectGroup,
  MultiSelectList,
  MultiSelectItem,
  parseMultiSelectValue,
  groupMultiSelectValues,
};
